import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../../components/AppLayout';
import api from '../../api/axiosInstance';

/* ─── Constants ──────────────────────────────────────────────────── */
const HEADER_H = 52;

const EVENT_TYPES = ['Bullish', 'Bearish', 'As per Trend', 'Sell On Rise', 'Buy On Dip', 'Reversal Day'];
const MOON_PHASES = ['', 'Amavasya', 'Poonam', 'Shukla Paksha', 'Krishna Paksha'];
const FEST_TYPES  = ['National', 'Religious', 'Regional', 'Bank Holiday', 'Market Holiday'];

const EVENT_COLORS = {
  'Bullish':      '#22c55e',
  'Bearish':      '#ef4444',
  'As per Trend': '#3b82f6',
  'Sell On Rise': '#dc2626',
  'Buy On Dip':   '#16a34a',
  'Reversal Day': '#f97316',
};

const MOON_ICONS = {
  'Amavasya':      '🌑',
  'Poonam':        '🌕',
  'Shukla Paksha': '🌒',
  'Krishna Paksha':'🌘',
};

const BLANK_EVENT = {
  date: '', stockName: '', indexName: '', moonPhase: '', eventType: '', description: '',
};
const BLANK_FEST = {
  date: '', name: '', type: 'Religious', description: '', isMarketHoliday: false,
};

/* ─── Helpers ────────────────────────────────────────────────────── */
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function toInputDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

/* ─── Toast ──────────────────────────────────────────────────────── */
function Toast({ toasts }) {
  return (
    <div style={S.toastContainer}>
      {toasts.map(t => (
        <div key={t.id} style={{ ...S.toast, ...S[`toast_${t.type}`] }}>
          {t.type === 'success' ? '✅' : t.type === 'error' ? '❌' : 'ℹ️'} {t.msg}
        </div>
      ))}
    </div>
  );
}

/* ─── Confirm Dialog ─────────────────────────────────────────────── */
function ConfirmDialog({ msg, onConfirm, onCancel }) {
  return (
    <div style={S.overlayBg}>
      <div style={S.confirmBox}>
        <p style={{ color: '#fff', marginBottom: 20, fontSize: '0.9rem' }}>{msg}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button style={S.btnGhost} onClick={onCancel}>Cancel</button>
          <button style={S.btnDanger} onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Event Form ─────────────────────────────────────────────────── */
function EventForm({ init, onSaved, onCancel }) {
  const [form, setForm] = useState(init || BLANK_EVENT);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => { setForm(init || BLANK_EVENT); setError(''); }, [init]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.eventType) { setError('Date and Event Type are required.'); return; }
    setSaving(true); setError('');
    try {
      if (form._id) {
        await api.put(`/astro-calendar/events/${form._id}`, form);
      } else {
        await api.post('/astro-calendar/events', form);
      }
      onSaved(form._id ? 'Event updated.' : 'Event created.');
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={S.form}>
      <div style={S.formTitle}>
        {form._id ? '✏️ Edit Event' : '➕ New Astro Event'}
      </div>

      {error && <div style={S.formError}>{error}</div>}

      <div style={S.formGroup}>
        <label style={S.label}>Date *</label>
        <input style={S.input} type="date" value={form.date ? toInputDate(form.date) : form.date}
          onChange={e => set('date', e.target.value)} required />
      </div>

      <div style={S.formRow}>
        <div style={S.formGroup}>
          <label style={S.label}>Stock Name</label>
          <input style={S.input} type="text" placeholder="e.g. NIFTY" value={form.stockName}
            onChange={e => set('stockName', e.target.value)} />
        </div>
        <div style={S.formGroup}>
          <label style={S.label}>Index Name</label>
          <input style={S.input} type="text" placeholder="e.g. BANKNIFTY" value={form.indexName}
            onChange={e => set('indexName', e.target.value)} />
        </div>
      </div>

      <div style={S.formRow}>
        <div style={S.formGroup}>
          <label style={S.label}>Event Type *</label>
          <select style={S.input} value={form.eventType} onChange={e => set('eventType', e.target.value)} required>
            <option value="" style={{ background: '#0f1629' }}>Select type…</option>
            {EVENT_TYPES.map(t => (
              <option key={t} value={t} style={{ background: '#0f1629' }}>{t}</option>
            ))}
          </select>
        </div>
        <div style={S.formGroup}>
          <label style={S.label}>Moon Phase</label>
          <select style={S.input} value={form.moonPhase} onChange={e => set('moonPhase', e.target.value)}>
            {MOON_PHASES.map(p => (
              <option key={p} value={p} style={{ background: '#0f1629' }}>{p || '— None —'}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={S.formGroup}>
        <label style={S.label}>Description</label>
        <textarea style={{ ...S.input, minHeight: 68, resize: 'vertical' }} placeholder="Optional notes…"
          value={form.description} onChange={e => set('description', e.target.value)} />
      </div>

      <div style={S.formBtns}>
        <button type="button" style={S.btnGhost} onClick={onCancel}>Cancel</button>
        <button type="submit" style={S.btnPrimary} disabled={saving}>
          {saving ? 'Saving…' : form._id ? 'Update Event' : 'Create Event'}
        </button>
      </div>
    </form>
  );
}

/* ─── Festival Form ──────────────────────────────────────────────── */
function FestivalForm({ init, onSaved, onCancel }) {
  const [form, setForm] = useState(init || BLANK_FEST);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => { setForm(init || BLANK_FEST); setError(''); }, [init]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.name) { setError('Date and Name are required.'); return; }
    setSaving(true); setError('');
    try {
      if (form._id) {
        await api.put(`/astro-calendar/festivals/${form._id}`, form);
      } else {
        await api.post('/astro-calendar/festivals', form);
      }
      onSaved(form._id ? 'Festival updated.' : 'Festival created.');
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={S.form}>
      <div style={S.formTitle}>
        {form._id ? '✏️ Edit Festival' : '➕ New Festival / Holiday'}
      </div>

      {error && <div style={S.formError}>{error}</div>}

      <div style={S.formGroup}>
        <label style={S.label}>Date *</label>
        <input style={S.input} type="date" value={form.date ? toInputDate(form.date) : form.date}
          onChange={e => set('date', e.target.value)} required />
      </div>

      <div style={S.formGroup}>
        <label style={S.label}>Festival Name *</label>
        <input style={S.input} type="text" placeholder="e.g. Diwali" value={form.name}
          onChange={e => set('name', e.target.value)} required />
      </div>

      <div style={S.formRow}>
        <div style={S.formGroup}>
          <label style={S.label}>Type</label>
          <select style={S.input} value={form.type} onChange={e => set('type', e.target.value)}>
            {FEST_TYPES.map(t => (
              <option key={t} value={t} style={{ background: '#0f1629' }}>{t}</option>
            ))}
          </select>
        </div>
        <div style={S.formGroup}>
          <label style={S.label}>Market Holiday?</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 38 }}>
            <input type="checkbox" id="isHoliday" checked={form.isMarketHoliday}
              onChange={e => set('isMarketHoliday', e.target.checked)}
              style={{ width: 18, height: 18, accentColor: '#667eea', cursor: 'pointer' }} />
            <label htmlFor="isHoliday" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem', cursor: 'pointer' }}>
              Yes, mark as holiday
            </label>
          </div>
        </div>
      </div>

      <div style={S.formGroup}>
        <label style={S.label}>Description</label>
        <textarea style={{ ...S.input, minHeight: 68, resize: 'vertical' }} placeholder="Optional notes…"
          value={form.description} onChange={e => set('description', e.target.value)} />
      </div>

      <div style={S.formBtns}>
        <button type="button" style={S.btnGhost} onClick={onCancel}>Cancel</button>
        <button type="submit" style={S.btnPrimary} disabled={saving}>
          {saving ? 'Saving…' : form._id ? 'Update Festival' : 'Create Festival'}
        </button>
      </div>
    </form>
  );
}

/* ─── Main Page ──────────────────────────────────────────────────── */
export default function AstroCalendarAdmin() {
  const [tab, setTab]             = useState('events');   // 'events' | 'festivals'
  const [events, setEvents]       = useState([]);
  const [festivals, setFestivals] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [searchQ, setSearchQ]     = useState('');
  const [filterType, setFilterType] = useState('');
  const [editItem, setEditItem]   = useState(null);   // null = new, obj = edit
  const [showForm, setShowForm]   = useState(false);
  const [toasts, setToasts]       = useState([]);
  const [confirm, setConfirm]     = useState(null);   // { id, type, label }

  /* ── Toast helper ── */
  const pushToast = useCallback((msg, type = 'success') => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
  }, []);

  /* ── Fetch ── */
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/astro-calendar/events');
      setEvents(res.data.data || []);
    } catch {
      pushToast('Failed to load events', 'error');
    } finally { setLoading(false); }
  }, [pushToast]);

  const fetchFestivals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/astro-calendar/festivals');
      setFestivals(res.data.data || []);
    } catch {
      pushToast('Failed to load festivals', 'error');
    } finally { setLoading(false); }
  }, [pushToast]);

  useEffect(() => {
    if (tab === 'events')    fetchEvents();
    else                      fetchFestivals();
  }, [tab, fetchEvents, fetchFestivals]);

  /* ── Form saved ── */
  const handleSaved = (msg) => {
    pushToast(msg);
    setShowForm(false);
    setEditItem(null);
    if (tab === 'events') fetchEvents();
    else fetchFestivals();
  };

  /* ── Delete ── */
  const requestDelete = (id, label) => setConfirm({ id, label });

  const confirmDelete = async () => {
    if (!confirm) return;
    try {
      if (tab === 'events') {
        await api.delete(`/astro-calendar/events/${confirm.id}/permanent`);
        setEvents(e => e.filter(x => x._id !== confirm.id));
      } else {
        await api.delete(`/astro-calendar/festivals/${confirm.id}`);
        setFestivals(f => f.filter(x => x._id !== confirm.id));
      }
      pushToast('Deleted successfully.');
    } catch (err) {
      pushToast(err?.response?.data?.message || 'Delete failed', 'error');
    } finally {
      setConfirm(null);
    }
  };

  /* ── Edit ── */
  const handleEdit = (item) => {
    setEditItem(item);
    setShowForm(true);
  };

  /* ── Filter ── */
  const filteredEvents = events.filter(e => {
    const q = searchQ.toLowerCase();
    const matchQ = !q || (e.stockName || '').toLowerCase().includes(q) ||
      (e.indexName || '').toLowerCase().includes(q) ||
      (e.description || '').toLowerCase().includes(q);
    const matchT = !filterType || e.eventType === filterType;
    return matchQ && matchT;
  });

  const filteredFests = festivals.filter(f => {
    const q = searchQ.toLowerCase();
    return !q || f.name.toLowerCase().includes(q) || (f.type || '').toLowerCase().includes(q);
  });

  /* ── Tab switch ── */
  const switchTab = (t) => {
    setTab(t); setShowForm(false); setEditItem(null);
    setSearchQ(''); setFilterType('');
  };

  return (
    <AppLayout pageBadge="Astro Calendar Admin">

      {/* ── Background ── */}
      <div style={S.bg} />

      {/* ── Shell ── */}
      <div style={{ ...S.shell, height: `calc(100vh - ${HEADER_H}px)` }}>

        {/* ══ Row 1 — Controls bar ═══════════════════════════════ */}
        <div style={S.controlsBar}>
          <div style={S.barLeft}>
            <span style={{ fontSize: '1.1rem' }}>🌙</span>
            <span style={S.pageTitle}>Astro Calendar Admin</span>
            <span style={S.adminBadge}>ADMIN</span>
          </div>

          {/* Tabs */}
          <div style={S.tabGroup}>
            <button
              style={{ ...S.tabBtn, ...(tab === 'events' ? S.tabBtnActive : {}) }}
              onClick={() => switchTab('events')}
            >📌 Astro Events ({events.length})</button>
            <button
              style={{ ...S.tabBtn, ...(tab === 'festivals' ? S.tabBtnActive : {}) }}
              onClick={() => switchTab('festivals')}
            >🎉 Festivals ({festivals.length})</button>
          </div>

          {/* Add button */}
          <button style={S.btnPrimary} onClick={() => { setEditItem(null); setShowForm(true); }}>
            + {tab === 'events' ? 'New Event' : 'New Festival'}
          </button>
        </div>

        {/* ══ Row 2 — Filters bar ════════════════════════════════ */}
        <div style={S.filtersBar}>
          <input
            style={S.searchInput}
            type="text"
            placeholder={tab === 'events' ? '🔍 Search stock, index…' : '🔍 Search festival…'}
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
          />
          {tab === 'events' && (
            <select style={S.filterSelect} value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="" style={{ background: '#0f1629' }}>All Types</option>
              {EVENT_TYPES.map(t => (
                <option key={t} value={t} style={{ background: '#0f1629' }}>{t}</option>
              ))}
            </select>
          )}
          <span style={S.countChip}>
            {tab === 'events' ? filteredEvents.length : filteredFests.length} records
          </span>
          {loading && <span style={S.loadingDot}>⟳ Loading…</span>}
        </div>

        {/* ══ Row 3 — Main content area ══════════════════════════ */}
        <div style={S.mainArea}>

          {/* ── Inline Form Panel (slides in on the left) ── */}
          {showForm && (
            <div style={S.formPanel}>
              {tab === 'events' ? (
                <EventForm
                  init={editItem}
                  onSaved={handleSaved}
                  onCancel={() => { setShowForm(false); setEditItem(null); }}
                />
              ) : (
                <FestivalForm
                  init={editItem}
                  onSaved={handleSaved}
                  onCancel={() => { setShowForm(false); setEditItem(null); }}
                />
              )}
            </div>
          )}

          {/* ── Table Panel ── */}
          <div style={S.tablePanel}>
            {tab === 'events' ? (
              <EventsTable
                rows={filteredEvents}
                onEdit={handleEdit}
                onDelete={requestDelete}
              />
            ) : (
              <FestivalsTable
                rows={filteredFests}
                onEdit={handleEdit}
                onDelete={requestDelete}
              />
            )}
          </div>

        </div>
      </div>

      {/* Toast */}
      <Toast toasts={toasts} />

      {/* Confirm delete */}
      {confirm && (
        <ConfirmDialog
          msg={`Delete "${confirm.label}"? This action cannot be undone.`}
          onConfirm={confirmDelete}
          onCancel={() => setConfirm(null)}
        />
      )}

    </AppLayout>
  );
}

/* ─── Events Table ───────────────────────────────────────────────── */
function EventsTable({ rows, onEdit, onDelete }) {
  if (!rows.length) {
    return (
      <div style={S.empty}>
        <span style={{ fontSize: '2.5rem', opacity: 0.2 }}>📭</span>
        <p style={{ color: 'rgba(255,255,255,0.35)', marginTop: 12, fontSize: '0.82rem' }}>No events found.</p>
      </div>
    );
  }
  return (
    <table style={S.table}>
      <thead>
        <tr>
          {['Date', 'Stock / Index', 'Event Type', 'Moon Phase', 'Description', 'Actions'].map(h => (
            <th key={h} style={S.th}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((ev, i) => (
          <tr key={ev._id} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
            <td style={S.td}>{fmtDate(ev.date)}</td>
            <td style={S.td}>
              <span style={{ color: '#fff' }}>{ev.stockName || '—'}</span>
              {ev.indexName && <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', display: 'block' }}>{ev.indexName}</span>}
            </td>
            <td style={S.td}>
              <span style={{
                ...S.typeBadge,
                background: EVENT_COLORS[ev.eventType] || '#6366f1',
              }}>{ev.eventType}</span>
            </td>
            <td style={S.td}>
              {ev.moonPhase
                ? <span style={S.moonChip}>{MOON_ICONS[ev.moonPhase] || ''} {ev.moonPhase}</span>
                : <span style={{ color: 'rgba(255,255,255,0.25)' }}>—</span>}
            </td>
            <td style={{ ...S.td, maxWidth: 200, color: 'rgba(255,255,255,0.55)', fontSize: '0.78rem' }}>
              <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {ev.description || '—'}
              </span>
            </td>
            <td style={S.td}>
              <div style={S.actionBtns}>
                <button style={S.btnEdit} onClick={() => onEdit(ev)} title="Edit">✏️</button>
                <button style={S.btnDel}  onClick={() => onDelete(ev._id, ev.stockName || ev.eventType)} title="Delete">🗑️</button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ─── Festivals Table ────────────────────────────────────────────── */
function FestivalsTable({ rows, onEdit, onDelete }) {
  if (!rows.length) {
    return (
      <div style={S.empty}>
        <span style={{ fontSize: '2.5rem', opacity: 0.2 }}>📭</span>
        <p style={{ color: 'rgba(255,255,255,0.35)', marginTop: 12, fontSize: '0.82rem' }}>No festivals found.</p>
      </div>
    );
  }
  return (
    <table style={S.table}>
      <thead>
        <tr>
          {['Date', 'Name', 'Type', 'Market Holiday', 'Description', 'Actions'].map(h => (
            <th key={h} style={S.th}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((f, i) => (
          <tr key={f._id} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
            <td style={S.td}>{fmtDate(f.date)}</td>
            <td style={{ ...S.td, fontWeight: 600, color: '#fff' }}>{f.name}</td>
            <td style={S.td}>
              <span style={S.festTypeBadge}>{f.type}</span>
            </td>
            <td style={S.td}>
              {f.isMarketHoliday
                ? <span style={S.holidayYes}>✓ Yes</span>
                : <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.78rem' }}>No</span>}
            </td>
            <td style={{ ...S.td, maxWidth: 200, color: 'rgba(255,255,255,0.55)', fontSize: '0.78rem' }}>
              <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {f.description || '—'}
              </span>
            </td>
            <td style={S.td}>
              <div style={S.actionBtns}>
                <button style={S.btnEdit} onClick={() => onEdit(f)} title="Edit">✏️</button>
                <button style={S.btnDel}  onClick={() => onDelete(f._id, f.name)} title="Delete">🗑️</button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ─── Styles ─────────────────────────────────────────────────────── */
const S = {
  bg: {
    position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
    background: 'linear-gradient(135deg, #0d0d1a 0%, #111827 50%, #0a1628 100%)',
  },
  shell: {
    position: 'relative', zIndex: 1,
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
    padding: '10px 14px 10px',
    marginTop: HEADER_H,
    gap: 7,
    color: '#fff',
    fontFamily: "'Poppins','Segoe UI',sans-serif",
  },

  /* ── Row 1: Controls bar ── */
  controlsBar: {
    flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 10,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 12, padding: '9px 16px',
  },
  barLeft: { display: 'flex', alignItems: 'center', gap: 8 },
  pageTitle: { fontSize: '0.95rem', fontWeight: 700 },
  adminBadge: {
    fontSize: '0.62rem', fontWeight: 700,
    background: 'linear-gradient(135deg,#ef4444,#dc2626)',
    color: '#fff', padding: '2px 8px', borderRadius: 20,
    letterSpacing: 0.8, boxShadow: '0 0 10px rgba(239,68,68,0.4)',
  },
  tabGroup: { display: 'flex', gap: 6 },
  tabBtn: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: 'rgba(255,255,255,0.6)', padding: '6px 16px',
    borderRadius: 8, fontSize: '0.8rem', fontWeight: 500,
    cursor: 'pointer', transition: 'all 0.2s',
  },
  tabBtnActive: {
    background: 'linear-gradient(135deg,#667eea,#764ba2)',
    borderColor: 'transparent', color: '#fff',
    boxShadow: '0 0 14px rgba(102,126,234,0.4)',
  },

  /* ── Row 2: Filters bar ── */
  filtersBar: {
    flexShrink: 0,
    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 10, padding: '7px 14px',
  },
  searchInput: {
    flex: 1, minWidth: 180,
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8, padding: '6px 12px',
    color: '#fff', fontSize: '0.8rem',
    outline: 'none',
  },
  filterSelect: {
    background: '#1e2340',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8, padding: '6px 12px',
    color: '#fff', fontSize: '0.8rem',
    cursor: 'pointer', outline: 'none',
  },
  countChip: {
    fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)',
    background: 'rgba(255,255,255,0.05)',
    padding: '4px 10px', borderRadius: 20, flexShrink: 0,
  },
  loadingDot: {
    fontSize: '0.72rem', color: '#a78bfa',
    animation: 'pulse 1.5s infinite',
  },

  /* ── Row 3: Main area ── */
  mainArea: {
    flex: 1, minHeight: 0,
    display: 'flex', gap: 10,
  },

  /* Form panel */
  formPanel: {
    flexShrink: 0, width: 380,
    background: 'rgba(13,30,54,0.9)',
    border: '1px solid rgba(99,102,241,0.2)',
    borderRadius: 12,
    overflowY: 'auto',
  },

  /* Table panel */
  tablePanel: {
    flex: 1, minWidth: 0,
    background: 'rgba(13,30,54,0.9)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 12,
    overflowY: 'auto',
  },

  /* ── Form internals ── */
  form: {
    padding: '20px 18px',
    display: 'flex', flexDirection: 'column', gap: 0,
  },
  formTitle: {
    fontSize: '0.88rem', fontWeight: 700,
    color: '#a78bfa', marginBottom: 16,
    paddingBottom: 12,
    borderBottom: '1px solid rgba(255,255,255,0.07)',
  },
  formError: {
    background: 'rgba(239,68,68,0.12)',
    border: '1px solid rgba(239,68,68,0.3)',
    color: '#fca5a5', borderRadius: 8,
    padding: '8px 12px', fontSize: '0.78rem',
    marginBottom: 12,
  },
  formGroup: { marginBottom: 14 },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 0 },
  label: {
    display: 'block', marginBottom: 5,
    color: 'rgba(255,255,255,0.55)', fontSize: '0.73rem', fontWeight: 500,
  },
  input: {
    width: '100%', padding: '9px 12px',
    background: '#0a1628',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 8, color: '#fff',
    fontSize: '0.82rem', outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  formBtns: {
    display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8,
  },

  /* ── Buttons ── */
  btnPrimary: {
    background: 'linear-gradient(135deg,#667eea,#764ba2)',
    color: '#fff', border: 'none',
    padding: '8px 20px', borderRadius: 8,
    fontSize: '0.82rem', fontWeight: 600,
    cursor: 'pointer', flexShrink: 0,
    boxShadow: '0 4px 15px rgba(102,126,234,0.3)',
    transition: 'all 0.2s',
  },
  btnGhost: {
    background: 'rgba(255,255,255,0.06)',
    color: 'rgba(255,255,255,0.7)',
    border: '1px solid rgba(255,255,255,0.1)',
    padding: '8px 16px', borderRadius: 8,
    fontSize: '0.82rem', cursor: 'pointer',
    transition: 'all 0.2s',
  },
  btnDanger: {
    background: 'linear-gradient(135deg,#ef4444,#dc2626)',
    color: '#fff', border: 'none',
    padding: '8px 18px', borderRadius: 8,
    fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
  },
  btnEdit: {
    background: 'rgba(99,102,241,0.15)',
    border: '1px solid rgba(99,102,241,0.3)',
    color: '#a78bfa', padding: '5px 10px',
    borderRadius: 6, cursor: 'pointer',
    fontSize: '0.82rem',
  },
  btnDel: {
    background: 'rgba(239,68,68,0.12)',
    border: '1px solid rgba(239,68,68,0.3)',
    color: '#f87171', padding: '5px 10px',
    borderRadius: 6, cursor: 'pointer',
    fontSize: '0.82rem',
  },

  /* ── Table ── */
  table: {
    width: '100%', borderCollapse: 'separate', borderSpacing: 0,
  },
  th: {
    padding: '10px 14px', textAlign: 'left',
    fontSize: '0.7rem', fontWeight: 600,
    color: 'rgba(255,255,255,0.5)',
    textTransform: 'uppercase', letterSpacing: 0.6,
    background: 'rgba(0,0,0,0.3)',
    borderBottom: '2px solid rgba(102,126,234,0.2)',
    position: 'sticky', top: 0, zIndex: 2,
  },
  td: {
    padding: '10px 14px',
    color: 'rgba(255,255,255,0.75)',
    fontSize: '0.82rem',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    verticalAlign: 'middle',
  },
  actionBtns: { display: 'flex', gap: 6 },

  typeBadge: {
    display: 'inline-block', color: '#fff',
    padding: '3px 10px', borderRadius: 20,
    fontSize: '0.68rem', fontWeight: 600,
  },
  moonChip: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)',
  },
  festTypeBadge: {
    display: 'inline-block',
    background: 'rgba(251,191,36,0.15)',
    border: '1px solid rgba(251,191,36,0.25)',
    color: '#fbbf24',
    padding: '2px 9px', borderRadius: 20,
    fontSize: '0.68rem', fontWeight: 600,
  },
  holidayYes: {
    display: 'inline-block',
    background: 'rgba(239,68,68,0.15)',
    border: '1px solid rgba(239,68,68,0.3)',
    color: '#f87171',
    padding: '2px 9px', borderRadius: 20,
    fontSize: '0.68rem', fontWeight: 600,
  },

  /* ── Empty ── */
  empty: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    minHeight: 240,
  },

  /* ── Toast ── */
  toastContainer: {
    position: 'fixed', bottom: 24, right: 24, zIndex: 200,
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  toast: {
    padding: '10px 18px', borderRadius: 10,
    fontSize: '0.82rem', fontWeight: 500,
    color: '#fff', minWidth: 220,
    boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
    animation: 'fadeInUp 0.3s ease',
  },
  toast_success: { background: 'linear-gradient(135deg,#16a34a,#15803d)' },
  toast_error:   { background: 'linear-gradient(135deg,#dc2626,#b91c1c)' },
  toast_info:    { background: 'linear-gradient(135deg,#2563eb,#1d4ed8)' },

  /* ── Confirm ── */
  overlayBg: {
    position: 'fixed', inset: 0, zIndex: 300,
    background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  confirmBox: {
    background: 'linear-gradient(135deg,#1a1a2e,#16213e)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: 14, padding: '28px 28px 22px',
    maxWidth: 380, width: '90%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  },
};
